import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericComponent } from '../generic/generic.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { CC_CONSTANTS, StrokeLineCap } from '../../assets/constants';
import { LegendConfig } from 'src/app/shared/components/map/map.component';
import {
    DefaultPolygonStyle,
    EmptyPolygonStyle,
    HatchImage,
    MapColors,
} from 'src/app/shared/components/map/map.constants';
import { convertNumber } from 'src/app/shared/all.helpers';
import { PathOptions } from 'leaflet';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexPlotOptions,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec4Orange,
    colorAec6Gray,
    colorAec7RedLight,
    colorAec8RedDark,
} from 'src/app/shared/all.constants';
import {
    LINKY_DEPLOYMENT_RATE_COMPARISON_CHART,
    LINKY_METER_DEPLOYMENT_RATE_MAP,
    METERS_KPIS,
    METERS_TYPOLOGY_CHART,
} from '../../assets/keys';

const KEYS = [
    METERS_KPIS,
    METERS_TYPOLOGY_CHART,
    LINKY_DEPLOYMENT_RATE_COMPARISON_CHART,
    LINKY_METER_DEPLOYMENT_RATE_MAP,
];

interface KpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    numberLinkyMeters?: number;
    numberLinkyMetersEvolution?: number;

    deploymentRate?: number;
    deploymentRateEvolution?: number;

    communicatingMetersRate?: number;
    communicatingMetersRateEvolution?: number;
}

interface MetersTypologyChartData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    linkyRate?: number;
    nonLinkyRate?: number;
    highCabinsCount?: number;
}

interface linkyDeploymentRateComparisonChartData {
    organizationId?: string;
    year?: string;
    category?: string;
    min?: number;
    max?: number;
    mean?: number;
    clientValue?: number;
    error?: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-meters',
    templateUrl: './meters.component.html',
    styleUrls: ['./meters.component.scss'],
})
export class MetersComponent extends GenericComponent {
    public texts = CC_CONSTANTS.transformerStations.tabs.meters.texts;

    public kpis: Signal<KpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(METERS_KPIS),
    ) as Signal<KpiData[]>;

    public metersTypologyChartData: Signal<MetersTypologyChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(METERS_TYPOLOGY_CHART),
    ) as Signal<MetersTypologyChartData[]>;
    public metersTypologyChartSeries: ApexAxisChartSeries = [];
    public metersTypologyDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({
            0: this.texts.metersTypologyChart.series[0].precision,
            1: this.texts.metersTypologyChart.series[1].precision,
            2: this.texts.metersTypologyChart.series[2].precision,
        }),
    };
    public metersTypologyXcategories: ApexXAxis['categories'];
    public metersTypologyYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.metersTypologyChart.series[0].name,
            min: 0,
            max: 100,
            title: {
                text: this.texts.metersTypologyChart.yaxisTitleRight,
            },
        },
        {
            seriesName: this.texts.metersTypologyChart.series[0].name,
            min: 0,
            max: 100,
        },
        {
            seriesName: this.texts.metersTypologyChart.series[2].name,
            min: 0,
            opposite: true,
            title: {
                text: this.texts.metersTypologyChart.yaxisTitleLeft,
            },
        },
    ];
    public metersTypologyChartError: boolean = true;
    public metersTypologyCustomTooltip: ApexTooltip['custom'];

    public linkyDeploymentRateComparisonChartSeries: ApexAxisChartSeries = [];
    public linkyDeploymentRateComparisonChartDataSignal: Signal<
        linkyDeploymentRateComparisonChartData[]
    > = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(LINKY_DEPLOYMENT_RATE_COMPARISON_CHART),
    ) as Signal<linkyDeploymentRateComparisonChartData[]>;

    public linkyDeploymentRateChartYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 80,
        },
    ];

    public rateComparisonDataLabels: ApexDataLabels = {
        enabled: false,
    };
    public rateComparisonPlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: true,
            barHeight: this.setRangeBarHeight(1),
            borderRadius: 1,
        },
    };
    public rateComparisonCustomTooltip: ApexTooltip['custom'] = function ({
        series,
        seriesIndex,
        dataPointIndex,
        w,
    }) {
        const precision =
            CC_CONSTANTS.transformerStations.tabs.meters.texts.linkyDeploymentRateComparisonChart
                .precision;
        const dataSerie = w.globals.initialSeries[seriesIndex];
        const serieName = dataSerie.name;
        const serieColor = dataSerie.color;
        const dataPoint = dataSerie.data[dataPointIndex];
        const min = convertNumber(dataPoint.y[0], precision, false);
        const max = convertNumber(dataPoint.y[1], precision, false);

        const meanGoal = dataPoint.goals[0];
        const meanValue = convertNumber(meanGoal.value, precision, false);
        const meanName = meanGoal.name;
        const meanColor = meanGoal.strokeColor;

        const clientValueGoal = dataPoint.goals[1];
        const clientValue = convertNumber(clientValueGoal.value, precision, false);
        const clientValueName = clientValueGoal.name;
        const clientColor = clientValueGoal.strokeColor;

        return `
                <div class="apexcharts-tooltip-title">
                    ${dataPoint.x}
                </div>

                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${serieColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${serieName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${min} - ${max} %</span>
                        </div>
                            <div class="apexcharts-tooltip-goals-group">
                            </div>
                    </div>
                </div>
                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 2;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${meanColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${meanName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${meanValue} %</span>
                        </div>
                            <div class="apexcharts-tooltip-goals-group">
                            </div>
                    </div>
                </div>
                    <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 3;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${clientColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${clientValueName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${clientValue} %</span>
                        </div>

                    </div>
                </div>
            `;
    };
    public linkyDeploymentRateComparisonChartError: boolean = true;

    public geometries: Signal<any[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(LINKY_METER_DEPLOYMENT_RATE_MAP),
    ) as Signal<any[]>;
    public legendConfig: LegendConfig[] = [
        {
            title: 'Taux de déploiement des compteurs C5 Linky',
            values: [
                { color: MapColors.red, value: 'Moins de 80%' },
                { color: MapColors.orange, value: '80 à 85%' },
                { color: MapColors.yellow, value: '85 à 90%' },
                { color: MapColors.paleGreen, value: '90 à 95%' },
                { color: MapColors.lightGreen, value: '95 à 99%' },
                { color: MapColors.green, value: 'Plus de 99%' },
                { color: `url(${HatchImage})`, value: 'Communes hors concession' },
            ],
        },
    ];

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const metersTypologyDataList = this.metersTypologyChartData();
            this.metersTypologyChartError = this.checkForError(metersTypologyDataList, [
                'xYear',
                'linkyRate',
                'nonLinkyRate',
                'highCabinsCount',
            ]);
            if (!this.metersTypologyChartError) {
                this.metersTypologyXcategories = metersTypologyDataList.map((data) => data.xYear);
                this.metersTypologyChartSeries = [
                    {
                        name: this.texts.metersTypologyChart.series[0].name,
                        type: 'column',
                        data: metersTypologyDataList.map((data) => data.linkyRate!),
                        color: colorAec2BlueDark,
                    },
                    {
                        name: this.texts.metersTypologyChart.series[1].name,
                        type: 'column',
                        data: metersTypologyDataList.map((data) => data.nonLinkyRate!),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.metersTypologyChart.series[2].name,
                        type: 'line',
                        data: metersTypologyDataList.map((data) => data.highCabinsCount!),
                        color: colorAec7RedLight,
                    },
                ];
                this.metersTypologyDataLabels.enabledOnSeries = [0, 1, 2];
            }

            const linkyDeploymentRateComparisonChartData =
                this.linkyDeploymentRateComparisonChartDataSignal();
            this.linkyDeploymentRateComparisonChartError = this.checkForError(
                linkyDeploymentRateComparisonChartData,
                ['category', 'min', 'max', 'mean'],
            );
            if (!this.linkyDeploymentRateComparisonChartError) {
                this.linkyDeploymentRateComparisonChartSeries = [
                    {
                        name: this.texts.linkyDeploymentRateComparisonChart.serieLabel,
                        data: linkyDeploymentRateComparisonChartData.map((data) => ({
                            x: data.category,
                            y: [data.min, data.max],
                            goals: [
                                {
                                    name: this.texts.linkyDeploymentRateComparisonChart
                                        .aecMeanLabel,
                                    value: data.mean!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec8RedDark,
                                },

                                {
                                    name: this.texts.linkyDeploymentRateComparisonChart
                                        .clientValueLabel,
                                    value: data.clientValue!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec1BlueLight,
                                },
                            ],
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.linkyDeploymentRateComparisonChart.serieLabelAvg,
                        type: 'bar',
                        data: [],
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.linkyDeploymentRateComparisonChart.serieLabelValue,
                        type: 'bar',
                        data: [],
                        color: colorAec1BlueLight,
                    },
                ];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.metersTypologyCustomTooltip = this.setMetersTypologyCustomTooltip();
    }

    private setMetersTypologyCustomTooltip(): ApexTooltip['custom'] {
        return ({ series, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const linkyPrecision = this.texts.metersTypologyChart.series[0].precision;
            const linkySerie = w.globals.initialSeries[0];
            const {
                name: linkyName,
                color: linkyColor,
                valueStringified: linkyStringified,
            } = this.setCustomTooltipData(linkyPrecision, linkySerie, dataPointIndex);

            const otherPrecision = this.texts.metersTypologyChart.series[1].precision;
            const otherSerie = w.globals.initialSeries[1];
            const {
                name: otherName,
                color: otherColor,
                valueStringified: otherStringified,
            } = this.setCustomTooltipData(otherPrecision, otherSerie, dataPointIndex);

            const highCabinePrecision = this.texts.metersTypologyChart.series[2].precision;
            const highCabineSerie = w.globals.initialSeries[2];
            const {
                name: highCabineName,
                color: highCabineColor,
                valueStringified: highCabineStringified,
            } = this.setCustomTooltipData(highCabinePrecision, highCabineSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: linkyColor,
                    name: linkyName,
                    value: linkyStringified + ' %',
                },
                {
                    color: otherColor,
                    name: otherName,
                    value: otherStringified + ' %',
                },
                {
                    color: highCabineColor,
                    name: highCabineName,
                    value: highCabineStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    public getMapStyle(feature: GeoJSON.Feature | undefined): PathOptions {
        if (!feature?.properties || feature.properties['linkyMeterDeploymentRate'] === null) {
            return EmptyPolygonStyle;
        }

        // Cities style
        let fillColor = MapColors.red;
        const linkyMeterDeploymentRate = Number(feature.properties['linkyMeterDeploymentRate']);
        if (linkyMeterDeploymentRate > 99) {
            fillColor = MapColors.green;
        } else if (linkyMeterDeploymentRate > 95) {
            fillColor = MapColors.lightGreen;
        } else if (linkyMeterDeploymentRate > 90) {
            fillColor = MapColors.paleGreen;
        } else if (linkyMeterDeploymentRate > 85) {
            fillColor = MapColors.yellow;
        } else if (linkyMeterDeploymentRate > 80) {
            fillColor = MapColors.orange;
        }

        return {
            ...DefaultPolygonStyle,
            fillColor,
        };
    }

    public getTooltip(feature: GeoJSON.Feature): string {
        if (!feature.properties) {
            return '';
        }
        let { cityName, linkyMeterDeploymentRate } = feature.properties;
        return `${cityName} - ${convertNumber(linkyMeterDeploymentRate)} % de déploiement des compteurs C5 Linky`;
    }
}
